import { Injectable } from '@angular/core';
import {
  AysClinicSnIdentifier,
  AysClinicSnSearchItem,
  AysClinicSnSearchPage,
  AysClinicSnSummary,
  ClinicReadService,
} from '@bindoc-projects/clinic-domain-lib';
import { Observable, of } from 'rxjs';

/**
 * {@link ClinicReadService} for a country that has NO clinic backend.
 *
 * Austria is the case this exists for: there is no Austrian clinics-api, and
 * `meta-at-app`'s config says so out loud — `AYS_CLINIC_RESOURCE_URL` is the
 * `https://not-a-real-api.de` placeholder. Until now AT nevertheless inherited the
 * DE-legacy `AysClinicService` as `provideAppCore`'s root default, so every clinic
 * read was a request to a host that does not resolve. This makes the absence
 * explicit instead (BDA-5809 step 4).
 *
 * Deliberately NOT a mock: fabricating Austrian clinics would put invented data in
 * a product surface, which is worse than showing none. And deliberately not a
 * redirect to the German clinics-api: Austrian users searching German clinics is
 * the wrong data, and it would need a real infra change plus an access decision.
 *
 * ## Behaviour, and why it is safe for today's consumers
 *
 * - `search` / `getAll` answer empty. AT's only clinic surface is the cube map
 *   tile's "Kartenmittelpunkt → Klinik" picker; with no results the user simply
 *   cannot pick a clinic, so the centre-by-clinic option is unavailable rather
 *   than broken.
 * - `get` echoes the caller's ref back with no name or address rather than
 *   throwing, because it is reachable from a resolver: `map-tile-resolver` reads
 *   `clinic?.name` / `clinic?.address?.location` and then drops entries without a
 *   location, so an empty summary degrades to "this layer has no clinics". An
 *   error would fail the route resolve instead.
 */
@Injectable({
  providedIn: 'root',
})
export class ClinicUnavailableReadService implements ClinicReadService {
  get(ref: AysClinicSnIdentifier): Observable<AysClinicSnSummary> {
    return of({ id: ref, name: null, address: null });
  }

  search(): Observable<AysClinicSnSearchPage> {
    return of({ items: [], total: 0 });
  }

  getAll(): Observable<AysClinicSnSearchItem[]> {
    return of([]);
  }
}
